import { Link, useNavigate } from "react-router-dom";

import { formatDisplayDateValue } from "../../../utils/dateDisplay";
import { ClientTable, Tabs } from "../../shared";
import {
  BulletList,
  DetailAccordionGroup,
  DetailAccordionSection,
  DetailKeyMessage,
  DetailMetadataStrip,
  DetailModalShell,
  StatusBadge,
} from "../AiInsightsRaidDetailParts";
import { getInsightEvidenceRoute } from "../evidenceNavigation";
import type {
  Day1ReadinessRecommendedActionDto,
  Day1ReadinessTopActivityDto,
  Day1ReadinessWorkstreamDto,
} from "./AiInsightsDay1ReadinessTypes";

export type AiInsightsDay1ReadinessWorkstreamDetailModalProps = {
  workstream: Day1ReadinessWorkstreamDto;
  lastUpdatedAt?: string | null;
  onClose: () => void;
};

interface WorkstreamActivityRow {
  rowKey: string;
  planningItemId: string;
  activity: string;
  status: string;
  owner: string;
  plannedEndDate: string;
  riskReason: string;
}

function formatDisplayValue(value?: string | number | null) {
  if (value == null || value === "") {
    return "Not available";
  }

  if (typeof value === "number") {
    return String(value);
  }

  return value
    .split(/[_-]/g)
    .map((part) => (part.length > 0 ? `${part[0]?.toUpperCase() ?? ""}${part.slice(1)}` : part))
    .join(" ");
}

function getExplanation(value?: string | null) {
  const explanation = value?.trim();
  return explanation ? explanation : "No explanation provided";
}

function buildRecommendedActionSummary(action: Day1ReadinessRecommendedActionDto) {
  const title = action.action ?? "Not available";
  const detailParts = [
    action.reason,
    action.timeframe != null ? `Timeframe: ${action.timeframe}` : null,
    action.owner != null ? `Owner: ${action.owner}` : null,
  ].filter(Boolean);

  return detailParts.length > 0 ? `${title} — ${detailParts.join(" · ")}` : title;
}

function mapActivitiesToRows(activities: Day1ReadinessTopActivityDto[]): WorkstreamActivityRow[] {
  return activities.map((activity, index) => ({
    rowKey: `${activity.planning_item_id ?? "activity"}-${index}`,
    planningItemId: activity.planning_item_id?.trim() || "Not available",
    activity: activity.activity ?? "Not available",
    status: activity.status ?? "Not available",
    owner: activity.owner ?? "Not available",
    plannedEndDate: formatDisplayDateValue(activity.planned_end_date, {
      fallback: "Not available",
    }),
    riskReason: getExplanation(activity.risk_reason),
  }));
}

export function AiInsightsDay1ReadinessWorkstreamDetailModal({
  workstream,
  lastUpdatedAt,
  onClose,
}: AiInsightsDay1ReadinessWorkstreamDetailModalProps) {
  const navigate = useNavigate();
  const activities = Array.isArray(workstream.top_activities_requiring_attention)
    ? workstream.top_activities_requiring_attention
    : [];
  const recommendedActions = Array.isArray(workstream.recommended_actions)
    ? workstream.recommended_actions
    : [];
  const recommendedActionItems = recommendedActions.map(buildRecommendedActionSummary);
  const activityRows = mapActivitiesToRows(activities);
  const statusSummary = getExplanation(workstream.status_summary);

  return (
    <DetailModalShell
      title={workstream.workstream_name ?? "Not available"}
      onClose={onClose}
      lastUpdatedAt={lastUpdatedAt}
    >
      <DetailMetadataStrip
        items={[
          {
            label: "AI readiness",
            value: <StatusBadge value={formatDisplayValue(workstream.ai_readiness_rag)} />,
            provenance: "AI assessed",
          },
          {
            label: "Rule-based readiness",
            value: <StatusBadge value={formatDisplayValue(workstream.rule_based_rag)} />,
            provenance: "Rule-based",
          },
        ]}
      />

      <DetailKeyMessage label="Readiness summary">{statusSummary}</DetailKeyMessage>

      <Tabs
        variant="underline"
        className="global-insights-overlay__risk-detail-tabs"
        items={[
          {
            key: "readiness",
            label: "Readiness",
            content: (
              <DetailAccordionGroup>
                <DetailAccordionSection heading="Impact" itemKey="workstream-impact">
                  <div className="global-insights-overlay__risk-detail-grey-box">
                    <p className="global-insights-overlay__risk-detail-sublabel">Impact to programme</p>
                    <p className="global-insights-overlay__risk-detail-text">
                      {workstream.impact_to_programme ?? "Not available"}
                    </p>
                    <p className="global-insights-overlay__risk-detail-sublabel">Impact to my role</p>
                    <p className="global-insights-overlay__risk-detail-text">
                      {workstream.impact_to_my_role ?? "Not available"}
                    </p>
                  </div>
                </DetailAccordionSection>
                <DetailAccordionSection heading="Recommended actions" itemKey="workstream-actions">
                  <BulletList items={recommendedActionItems} />
                </DetailAccordionSection>
              </DetailAccordionGroup>
            ),
          },
          {
            key: "activities",
            label: "Activities requiring attention",
            content: (
              <DetailAccordionGroup>
                <DetailAccordionSection
                  heading="Activities requiring attention"
                  itemKey="workstream-activities"
                  badge={activityRows.length}
                >
                  <ClientTable
                    className="global-insights-overlay__day1-activity-table"
                    data={activityRows}
                    rowKey={(row) => row.rowKey}
                    condensed
                    rowClassName={(row) =>
                      row.planningItemId !== "Not available"
                        ? "global-insights-overlay__day1-activity-row"
                        : undefined
                    }
                    onRowClick={(row) => {
                      if (row.planningItemId !== "Not available") {
                        const route = getInsightEvidenceRoute(row.planningItemId, "planning");
                        if (route != null) {
                          navigate(route);
                        }
                      }
                    }}
                    columns={[
                      {
                        id: "planningItemId",
                        header: "ID",
                        width: "14%",
                        renderCell: (row) => {
                          const itemRoute =
                            row.planningItemId === "Not available"
                              ? undefined
                              : getInsightEvidenceRoute(row.planningItemId, "planning");

                          return itemRoute == null ? (
                            row.planningItemId
                          ) : (
                            <Link
                              className="global-insights-overlay__risk-detail-evidence-link global-insights-overlay__day1-activity-link"
                              to={itemRoute}
                              onClick={(event) => event.stopPropagation()}
                            >
                              {row.planningItemId}
                            </Link>
                          );
                        },
                      },
                      { id: "activity", header: "Activity", accessor: "activity", width: "20%" },
                      { id: "status", header: "Status", accessor: "status", width: "11%" },
                      { id: "owner", header: "Owner", accessor: "owner", width: "12%" },
                      {
                        id: "plannedEndDate",
                        header: "Planned end date",
                        accessor: "plannedEndDate",
                        width: "15%",
                      },
                      { id: "riskReason", header: "Risk reason", accessor: "riskReason", width: "28%" },
                    ]}
                  />
                  {activityRows.length === 0 && (
                    <p className="global-insights-overlay__risk-detail-text">No activities available.</p>
                  )}
                </DetailAccordionSection>
              </DetailAccordionGroup>
            ),
          },
        ]}
      />
    </DetailModalShell>
  );
}

export default AiInsightsDay1ReadinessWorkstreamDetailModal;
